import React, { useState } from 'react';

import { Dropdown } from 'primereact/dropdown';
import { InputNumber } from 'primereact/inputnumber';
import { InputText } from 'primereact/inputtext';

import { Calendar } from 'primereact/calendar';
import { Slider } from 'primereact/slider';
import { TriStateCheckbox } from 'primereact/tristatecheckbox';
import { Button } from 'primereact/button';
import { Checkbox } from 'primereact/checkbox';
import i18n from '../../../config/i18n';
import moment from 'moment';

export const textFilterTemplate = (options, placeholder) => {
  return (
    <InputText
      value={options.value}
      onChange={(e) => options.filterCallback(e.target.value, options.index)}
      onKeyDown={(e) => {
        if (e.key === 'Enter') {
          e.preventDefault();
          const overlay = e.target.closest('.p-column-filter-overlay');
          if (overlay) {
            const applyBtn = overlay.querySelector(
              '.p-column-filter-buttonbar .p-button:not(.p-button-outlined)',
            );
            applyBtn?.click();
          }
        }
      }}
      placeholder={placeholder || i18n.t('common.search')}
      className="p-column-filter"
    />
  );
};

export const dateFilterTemplate = (options) => {
  return (
    <Calendar
      value={options.value}
      onChange={(e) => options.filterCallback(e.value, options.index)}
      dateFormat="dd M yy"
      placeholder="dd M yy"
      mask="99/99/9999"
      locale={i18n.language}
    />
  );
};

export const dateRangeFilterTemplate = (options) => {
  // Normalize value to always be an array or null
  const value = Array.isArray(options.value) ? options.value : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
      <Calendar
        inputStyle={{ width: '130px' }}
        value={value?.[0] || null}
        onChange={(e) => {
          const currentValue = value || [null, null];
          const newValue = [e.value, currentValue[1]];
          // If both values are null, pass null instead of [null, null]
          const filterValue = !newValue[0] && !newValue[1] ? null : newValue;
          options.filterCallback(filterValue, options.index);
        }}
        dateFormat="dd M yy"
        locale={i18n.language}
        placeholder={i18n.t('from')}
        maxDate={value?.[1] || new Date()}
        showIcon
      />
      <Calendar
        inputStyle={{ width: '130px' }}
        value={value?.[1] || null}
        onChange={(e) => {
          const currentValue = value || [null, null];
          const newValue = [currentValue[0], e.value];
          // If both values are null, pass null instead of [null, null]
          const filterValue = !newValue[0] && !newValue[1] ? null : newValue;
          options.filterCallback(filterValue, options.index);
        }}
        dateFormat="dd M yy"
        locale={i18n.language}
        placeholder={i18n.t('to')}
        minDate={value?.[0]}
        maxDate={new Date()}
        showIcon
      />
    </div>
  );
};

// Date range filter that allows future dates (useful for expiry dates, etc.)
export const dateRangeFilterTemplateWithFuture = (options) => {
  // Normalize value to always be an array or null
  const value = Array.isArray(options.value) ? options.value : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
      <Calendar
        inputStyle={{ width: '130px' }}
        value={value?.[0] || null}
        onChange={(e) => {
          const currentValue = value || [null, null];
          const newValue = [e.value, currentValue[1]];
          // If both values are null, pass null instead of [null, null]
          const filterValue = !newValue[0] && !newValue[1] ? null : newValue;
          options.filterCallback(filterValue, options.index);
        }}
        dateFormat="dd M yy"
        placeholder={i18n.t('from')}
        maxDate={value?.[1] || null}
        locale={i18n.language}
        showIcon
      />
      <Calendar
        inputStyle={{ width: '130px' }}
        value={value?.[1] || null}
        onChange={(e) => {
          const currentValue = value || [null, null];
          const newValue = [currentValue[0], e.value];
          // If both values are null, pass null instead of [null, null]
          const filterValue = !newValue[0] && !newValue[1] ? null : newValue;
          options.filterCallback(filterValue, options.index);
        }}
        dateFormat="dd M yy"
        placeholder={i18n.t('to')}
        locale={i18n.language}
        minDate={value?.[0]}
        showIcon
      />
    </div>
  );
};

export const timeRangeFilterTemplate = (options) => {
  // Normalize value to always be an array or null
  const value = Array.isArray(options.value) ? options.value : null;

  return (
    <div style={{ display: 'flex', flexDirection: 'row', gap: '8px' }}>
      <Calendar
        inputStyle={{ flex: 1 }}
        value={value?.[0] || null}
        onChange={(e) => {
          const currentValue = value || [null, null];
          // Set seconds and milliseconds to zero
          const timeValue = e.value ? new Date(e.value) : null;
          if (timeValue) {
            timeValue.setSeconds(0, 0);
          }
          const newValue = [timeValue, currentValue[1]];
          const filterValue = !newValue[0] && !newValue[1] ? null : newValue;
          options.filterCallback(filterValue, options.index);
        }}
        timeOnly
        placeholder={i18n.t('from')}
        hourFormat="12"
        locale={i18n.language}
      />
      <Calendar
        inputStyle={{ flex: 1 }}
        value={value?.[1] || null}
        onChange={(e) => {
          const currentValue = value || [null, null];
          // Set seconds and milliseconds to zero
          const timeValue = e.value ? new Date(e.value) : null;
          if (timeValue) {
            timeValue.setSeconds(0, 0);
          }
          const newValue = [currentValue[0], timeValue];
          const filterValue = !newValue[0] && !newValue[1] ? null : newValue;
          options.filterCallback(filterValue, options.index);
        }}
        timeOnly
        placeholder={i18n.t('to')}
        hourFormat="12"
        minDate={value?.[0]}
        locale={i18n.language}
      />
    </div>
  );
};

export const currencyFilterTemplate = (options) => {
  return (
    <InputNumber
      value={options.value}
      onChange={(e) => options.filterCallback(e.value, options.index)}
      mode="currency"
      currency="USD"
      locale="en-US"
    />
  );
};

export const singleSelectFilterTemplate = (
  options,
  dropdownOptions,
  template,
  placeholder,
) => {
  return (
    <Dropdown
      value={options.value}
      options={dropdownOptions}
      onChange={(e) => options.filterCallback(e.value, options.index)}
      itemTemplate={template}
      placeholder={placeholder || i18n.t('select-one')}
      className="p-column-filter"
      showClear
    />
  );
};

export const sliderFilterTemplate = (options) => {
  return (
    <React.Fragment>
      <Slider
        value={options.value}
        onChange={(e) => options.filterCallback(e.value)}
        range
        className="m-3"
      />
      <div className="flex align-items-center justify-content-between px-2">
        <span>{options.value ? options.value[0] : 0}</span>
        <span>{options.value ? options.value[1] : 100}</span>
      </div>
    </React.Fragment>
  );
};

export const checkFilterTemplate = (options) => {
  return (
    <div className="flex align-items-center gap-2">
      <label htmlFor="verified-filter" className="font-bold">
        Verified
      </label>
      <TriStateCheckbox
        inputId="verified-filter"
        value={options.value}
        onChange={(e) => options.filterCallback(e.value)}
      />
    </div>
  );
};

const MultiSelectFilter = ({ options, dropdownOptions, template }) => {
  // Normalize value to always be an array or null
  const value = Array.isArray(options.value) ? options.value : [];
  const [isExpanded, setIsExpanded] = useState(true);

  const handleCheckboxChange = (optionValue) => {
    const newValue = value.includes(optionValue)
      ? value.filter((v) => v !== optionValue)
      : [...value, optionValue];

    // Update filter immediately
    const filterValue = newValue.length > 0 ? newValue : null;
    options.filterCallback(filterValue, options.index);
  };

  const getLabel = () => {
    return i18n.t('select_column', { column: options.placeholder });
  };

  return (
    <div style={{ minWidth: '240px' }}>
      <Button
        text
        className="w-100 justify-content-between px-3 py-2"
        onClick={() => setIsExpanded(!isExpanded)}
        style={{
          fontSize: '14px',
          border: '1px solid #dee2e6',
          borderRadius: '4px',
          marginBottom: '8px',
        }}
      >
        <span style={{ color: value.length > 0 ? '#495057' : '#6c757d' }}>
          {getLabel()}
        </span>
        <i
          className={`pi ${isExpanded ? 'pi-chevron-up' : 'pi-chevron-down'}`}
        />
      </Button>

      {isExpanded && (
        <div
          style={{
            border: '1px solid #dee2e6',
            borderRadius: '4px',
            padding: '12px',
          }}
        >
          {dropdownOptions.map((option) => {
            const optionValue =
              option.value !== undefined ? option.value : option;
            const optionLabel =
              option.label !== undefined ? option.label : option;

            return (
              <div
                key={optionValue}
                className="flex align-items-center mb-2"
                style={{ gap: '8px' }}
              >
                <Checkbox
                  inputId={`checkbox-${optionValue}`}
                  checked={value.includes(optionValue)}
                  onChange={() => handleCheckboxChange(optionValue)}
                />
                <label
                  htmlFor={`checkbox-${optionValue}`}
                  style={{ cursor: 'pointer', userSelect: 'none' }}
                >
                  {optionLabel}
                </label>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export const multiSelectFilterTemplate = (
  options,
  dropdownOptions,
  template,
) => {
  return (
    <MultiSelectFilter
      options={options}
      dropdownOptions={dropdownOptions}
      template={template}
    />
  );
};

// Wrapper to add sort options to filter menus
export const filterWithSortTemplate = (
  options,
  filterContent,
  fieldName,
  onSort,
  currentSortField,
  currentSortOrder,
  sortLabels = {
    asc: i18n.t('sort_asc'),
    desc: i18n.t('sort_desc'),
    ascIcon: 'pi-sort-amount-down',
    descIcon: 'pi-sort-amount-up',
  },
) => {
  const handleSort = (order) => {
    if (onSort) {
      // If clicking on the already active sort, clear it
      if (currentSortField === fieldName && currentSortOrder === order) {
        onSort(null, null);
      } else {
        onSort(fieldName, order);
      }
    }
  };

  const isAscActive = currentSortField === fieldName && currentSortOrder === 1;
  const isDescActive =
    currentSortField === fieldName && currentSortOrder === -1;

  return (
    <div style={{ minWidth: '240px' }}>
      <div
        style={{
          borderBottom: '1px solid #e9ecef',
          paddingBottom: '8px',
          marginBottom: '8px',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <Button
          text
          className="w-100 justify-content-start px-3 py-2"
          onClick={() => handleSort(1)}
          style={{
            gap: '8px',
            fontSize: '14px',
            backgroundColor: isAscActive ? '#e3f2fd' : 'transparent',
            fontWeight: isAscActive ? '600' : 'normal',
          }}
        >
          <i className={`pi ${sortLabels.ascIcon} mr-2`} />
          {sortLabels.asc}
          {isAscActive && <i className="pi pi-check ml-auto" />}
        </Button>
        <Button
          text
          className="w-100 justify-content-start px-3 py-2"
          onClick={() => handleSort(-1)}
          style={{
            gap: '8px',
            fontSize: '14px',
            backgroundColor: isDescActive ? '#e3f2fd' : 'transparent',
            fontWeight: isDescActive ? '600' : 'normal',
          }}
        >
          <i className={`pi ${sortLabels.descIcon} mr-2`} />
          {sortLabels.desc}
          {isDescActive && <i className="pi pi-check ml-auto" />}
        </Button>
      </div>
      <div className="py-2">{filterContent}</div>
    </div>
  );
};

// Date-specific sort labels
export const DATE_SORT_LABELS = () => ({
  asc: i18n.t('sort-oldest-newest'),
  desc: i18n.t('sort-newest-oldest'),
  ascIcon: 'pi-sort-amount-up',
  descIcon: 'pi-sort-amount-down',
});

/**
 * Helper function for date range filtering
 * @param {string} value - The date value from the row data
 * @param {Array|null} filter - The filter value [startDate, endDate] or null
 * @param {string} sourceFormat - The format of the source date (default: 'YYYY-MM-DD hh:mm:ss')
 * @returns {boolean} - Whether the row should be included in the filtered results
 */
export const dateRangeFilterFunction = (
  value,
  filter,
  sourceFormat = 'YYYY-MM-DD hh:mm:ss',
) => {
  if (!filter || (!filter[0] && !filter[1])) {
    return true;
  }
  if (!value || value === '-') {
    return false;
  }
  const date = moment(value, sourceFormat);
  const startDate = filter[0] ? moment(filter[0]).startOf('day') : null;
  const endDate = filter[1] ? moment(filter[1]).endOf('day') : null;

  if (startDate && endDate) {
    return date.isBetween(startDate, endDate, null, '[]');
  } else if (startDate) {
    return date.isSameOrAfter(startDate);
  } else if (endDate) {
    return date.isSameOrBefore(endDate);
  }
  return true;
};

/**
 * Helper function for time range filtering
 * @param {string} value - The time value from the row data (format: HH:mm:ss)
 * @param {Array|null} filter - The filter value [startTime, endTime] or null
 * @returns {boolean} - Whether the row should be included in the filtered results
 */
export const timeRangeFilterFunction = (value, filter) => {
  if (!filter || (!filter[0] && !filter[1])) {
    return true;
  }
  if (!value) {
    return false;
  }
  // Parse the time from the value (format: HH:mm:ss)
  const [hours, minutes, seconds] = value.split(':').map(Number);
  const timeDate = new Date();
  timeDate.setHours(hours, minutes, seconds || 0, 0);

  const startTime = filter[0] ? new Date(filter[0]) : null;
  const endTime = filter[1] ? new Date(filter[1]) : null;

  if (startTime && endTime) {
    return timeDate >= startTime && timeDate <= endTime;
  } else if (startTime) {
    // When only "from" is set, include all times from startTime up to 11:59:59 PM inclusive
    const endOfDay = new Date(timeDate);
    endOfDay.setHours(23, 59, 59, 0);
    return timeDate >= startTime && timeDate <= endOfDay;
  } else if (endTime) {
    return timeDate <= endTime;
  }
  return true;
};
